// Couche métier abonnement (Bloc 7) — source de vérité : Supabase.
//
// Le frontend ne calcule plus l'abonnement : il lit l'abonnement réel du compte
// authentifié via la RPC `abonnement_du_compte()` (formule, périodicité, dates,
// statut, limites et usage), et il agit via les RPC serveur `choisir_formule()`
// et `renouveler_abonnement()` qui appliquent toutes les règles côté base.
//
// Aucune donnée fictive : par défaut, un compte sans abonnement est sur la
// formule « Gratuit » (conforme aux limites serveur).

import { supabase } from "@/store/shared/supabase";
import type { FormuleId, Periodicite } from "@/config/formules";

export interface AbonnementUsage {
  proprietaires: number;
  immeubles: number;
  biens: number;
  locataires: number;
  contrats: number;
  employes: number;
}

export interface AbonnementFormule {
  id: string;
  code: string;
  nom: string;
  prix_mensuel: number;
  prix_annuel: number;
  limites: Partial<Record<keyof AbonnementUsage, number | null>> | null;
}

export interface AbonnementBrut {
  id: string;
  formule_id: string;
  periodicite: string;
  statut: string;
  date_debut: string;
  date_fin: string;
  montant: number;
}

export interface MonAbonnement {
  connecte: boolean;
  actif: boolean;
  expire: boolean;
  scope: string;
  est_super_admin?: boolean;
  abonnement: AbonnementBrut | null;
  formule: AbonnementFormule | null;
  /** Offre de lancement : l'abonnement courant est un essai gratuit (formule `essai`). */
  est_essai?: boolean;
  /** Formule choisie à l'inscription, conservée dans `comptes.formule_id`. */
  formule_choisie?: AbonnementFormule | null;
  /** Date à laquelle la formule a été choisie (comptes.formule_choisie_at). */
  formule_choisie_at?: string | null;
  /** Date limite de validation du paiement (formule choisie + 7 jours). */
  date_limite_paiement?: string | null;
  /** Demande de paiement réelle la plus récente rattachée à l'abonnement. */
  paiement_demande?: PaiementDemandeBrut | null;
  usage: AbonnementUsage | null;
}

export interface PaiementDemandeBrut {
  id: string;
  statut: string;
  montant: number;
  mode_paiement: string;
  reference: string | null;
  preuve_url: string | null;
  commentaire: string | null;
  date_paiement: string | null;
  date_validation: string | null;
}

export type DemandePaiementStatut = "en_attente" | "valide" | "rejete" | "annule";

export interface DemandePaiement {
  id: string;
  statut: DemandePaiementStatut;
  montant: number;
  modePaiement: string;
  reference: string;
  /** Preuve rattachée (chemin Storage), vide tant qu'aucune preuve n'est liée. */
  preuveUrl: string;
  commentaire: string;
  datePaiement: string | null;
  dateValidation: string | null;
}

const DEMANDE_STATUTS: DemandePaiementStatut[] = ["en_attente", "valide", "rejete", "annule"];

export function demandePaiementFromRpc(data: MonAbonnement | null): DemandePaiement | null {
  const raw = data?.paiement_demande;
  if (!raw || !raw.id) return null;
  const statut = (DEMANDE_STATUTS as string[]).includes(raw.statut)
    ? (raw.statut as DemandePaiementStatut)
    : "en_attente";
  return {
    id: raw.id,
    statut,
    montant: typeof raw.montant === "number" ? raw.montant : Number(raw.montant ?? 0) || 0,
    modePaiement: typeof raw.mode_paiement === "string" ? raw.mode_paiement : "",
    reference: typeof raw.reference === "string" ? raw.reference : "",
    preuveUrl: typeof raw.preuve_url === "string" ? raw.preuve_url : "",
    commentaire: typeof raw.commentaire === "string" ? raw.commentaire : "",
    datePaiement: typeof raw.date_paiement === "string" ? raw.date_paiement : null,
    dateValidation: typeof raw.date_validation === "string" ? raw.date_validation : null,
  };
}

/** Lit l'abonnement réel du compte authentifié (RPC serveur). */
export async function fetchMonAbonnement(): Promise<MonAbonnement | null> {
  const { data, error } = await supabase.rpc("abonnement_du_compte");
  if (error) throw error;
  return (data as MonAbonnement) ?? null;
}

/** Enregistre un choix/changement de formule côté serveur. */
export async function choisirFormuleRpc(code: FormuleId, periodicite: Periodicite): Promise<MonAbonnement | null> {
  const { data, error } = await supabase.rpc("choisir_formule", {
    p_formule_code: code,
    p_periodicite: periodicite,
  });
  if (error) throw error;
  return (data as MonAbonnement) ?? null;
}

/**
 * Rattache la preuve à la demande de paiement en attente du compte (RPC serveur).
 * Le serveur identifie la demande du compte authentifié et n'agit que si elle est
 * `en_attente` et sans preuve : jamais de création de paiement, jamais de doublon.
 */
export async function joindrePreuveAbonnementRpc(preuveUrl: string): Promise<void> {
  const { error } = await supabase.rpc("joindre_preuve_abonnement", { p_preuve_url: preuveUrl });
  if (error) throw error;
}

/** Prolonge l'abonnement courant d'une période (RPC serveur). */
export async function renouvelerAbonnementRpc(): Promise<MonAbonnement | null> {
  const { data, error } = await supabase.rpc("renouveler_abonnement");
  if (error) throw error;
  return (data as MonAbonnement) ?? null;
}

/** `mensuelle` (base) → `mensuel` (frontend). */
export function periodiciteFromDb(value: string | null | undefined): Periodicite {
  return value === "annuelle" ? "annuel" : "mensuel";
}

/** Statut DB + indicateur d'expiration → statut affiché dans les espaces. */
export function abonnementStatutFromDb(
  statutDb: string | null | undefined,
  expire: boolean
): "actif" | "brouillon" | "expire" | "resilie" {
  if (expire) return "expire";
  switch (statutDb) {
    case "actif":
      return "actif";
    case "en_attente":
      return "brouillon";
    case "suspendu":
    case "annule":
      return "resilie";
    case "expire":
      return "expire";
    default:
      return "brouillon";
  }
}

/** Code de formule à partir de la RPC (repli sur « gratuit »). */
export function formuleCodeFromRpc(data: MonAbonnement | null): FormuleId {
  const code = data?.formule?.code;
  if (code === "gratuit" || code === "essentiel" || code === "professionnel" || code === "agence") {
    return code;
  }
  return "gratuit";
}

/** Code de la formule choisie (comptes.formule_id), ou `null` si non renseignée. */
export function formuleChoisieCodeFromRpc(data: MonAbonnement | null): FormuleId | null {
  const code = data?.formule_choisie?.code;
  if (code === "gratuit" || code === "essentiel" || code === "professionnel" || code === "agence") {
    return code;
  }
  return null;
}

/**
 * Détecte un essai gratuit ACTIF (offre de lancement 6 mois).
 *
 * La formule technique `essai` est renvoyée par la RPC `abonnement_du_compte()`
 * tant que l'essai est en cours (statut `actif`, non expiré) : elle accorde un
 * accès complet (toutes les limites à `null` côté serveur).
 */
export function estEssaiActif(data: MonAbonnement | null): boolean {
  if (!data || !data.connecte) return false;
  return data.formule?.code === "essai" && data.abonnement?.statut === "actif" && data.expire !== true;
}

/** Nombre de jours calendaires restants avant la fin de l'essai (>= 0). */
export function joursRestantsEssai(data: MonAbonnement | null): number | null {
  const fin = data?.abonnement?.date_fin;
  if (!fin) return null;
  const finDate = new Date(`${fin}T00:00:00`);
  if (Number.isNaN(finDate.getTime())) return null;
  const aujourdhui = new Date();
  aujourdhui.setHours(0, 0, 0, 0);
  return Math.max(0, Math.ceil((finDate.getTime() - aujourdhui.getTime()) / 86400000));
}